import { useTranslation } from 'react-i18next';
import { traduzirTarefas as tt } from './tarefasI18n';
import React, { useEffect, useId, useMemo, useState, useSyncExternalStore } from 'react';
import api from '../../services/api';
import SearchableSelect from '../../components/SearchableSelect';
import { notifyToast } from '../../contexts/ToastContext';
import useFocoPreso from '../../hooks/useFocoPreso';
import AvatarUsuario from '../../components/AvatarUsuario';
import { corDoUsuario, iniciais } from '../../utils/avatarUsuario';
import {
  PRIORIDADES,
  STATUS,
  atrasada,
  darPlay,
  formatarData,
  formatarDuracao,
  mensagemDeErro,
  minutosParaTexto,
  pausarPlay,
  rotuloPrioridade,
  rotuloStatus,
  textoParaMinutos,
  lideraEquipe
} from './tarefasUtils';

// Relogio unico das telas de tarefas: so as tarefas com play rodando se
// inscrevem, e o intervalo so existe enquanto houver alguma. A cada tique so os
// numeros de tempo re-renderizam; listas e cartoes ficam como estao.
const ouvintesDoRelogio = new Set();
let tiqueDoRelogio = null;
let agoraDoRelogio = Date.now();
const lerRelogio = () => agoraDoRelogio;
const inscreverNoRelogio = (ouvinte) => {
  ouvintesDoRelogio.add(ouvinte);
  if (!tiqueDoRelogio) {
    tiqueDoRelogio = setInterval(() => {
      agoraDoRelogio = Date.now();
      ouvintesDoRelogio.forEach((o) => o());
    }, 5000);
  }
  return () => {
    ouvintesDoRelogio.delete(ouvinte);
    if (!ouvintesDoRelogio.size) { clearInterval(tiqueDoRelogio); tiqueDoRelogio = null; }
  };
};
const semInscricao = () => () => {};

// Quando cada tarefa (objeto vindo da API) chegou: o tempo_segundos dela vale
// ate esse instante. Nova busca = novo objeto = nova base.
const recebidaEm = new WeakMap();

export const rodandoAgora = (tarefa) => (tarefa.em_andamento_por || []).length > 0;

// Tempo apontado ao vivo: o total da API mais o que passou desde a busca, vezes
// quantas pessoas estao com o play ligado nela.
export const useTempoAoVivo = (tarefa) => {
  if (!recebidaEm.has(tarefa)) recebidaEm.set(tarefa, Date.now());
  const rodando = (tarefa.em_andamento_por || []).length;
  const agora = useSyncExternalStore(rodando ? inscreverNoRelogio : semInscricao, lerRelogio);
  const base = Number(tarefa.tempo_segundos) || 0;
  if (!rodando) return base;
  return base + Math.max(0, (agora - recebidaEm.get(tarefa)) / 1000) * rodando;
};

// Busca de novo a cada `intervalo` sem mostrar carregando (o React so troca o
// que mudou), para aparecer o play que outra pessoa ligou ou pausou. Com a aba
// do navegador escondida nao busca; ao voltar, busca na hora.
export const useAtualizacaoSilenciosa = (carregar, intervalo = 60000) => {
  useEffect(() => {
    let tique = null;
    const iniciar = () => { if (!tique) tique = setInterval(carregar, intervalo); };
    const parar = () => { clearInterval(tique); tique = null; };
    const aoMudarVisibilidade = () => {
      if (document.hidden) parar();
      else { carregar(); iniciar(); }
    };
    if (!document.hidden) iniciar();
    document.addEventListener('visibilitychange', aoMudarVisibilidade);
    return () => { parar(); document.removeEventListener('visibilitychange', aoMudarVisibilidade); };
  }, [carregar, intervalo]);
};

// Icones em SVG: os caracteres ▶ e ⏸ mudam de tamanho e de alinhamento
// conforme a fonte e nao ficam centralizados no botao redondo.
export const IconePlay = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    {/* Triangulo posicionado pelo centroide (centro visual), nao pela caixa: parece centralizado. */}
    <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.4-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z" fill="currentColor" />
  </svg>
);

export const IconePausa = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <rect x="6" y="5" width="4" height="14" rx="1.2" fill="currentColor" />
    <rect x="14" y="5" width="4" height="14" rx="1.2" fill="currentColor" />
  </svg>
);

// Icones dos botoes pequenos (editar, excluir, fixar, menu). Os caracteres
// ✎, 🗑, ★ e ⋯ nao existem na fonte do sistema (Altone): o navegador pega
// de outra fonte (Segoe UI Emoji/Symbol), com outra altura de linha, e o
// simbolo fica acima ou abaixo do centro do botao. Em SVG o desenho e sempre
// centralizado.
const Svg = ({ children, tamanho = 16, preenchido = false }) => (
  <svg viewBox="0 0 24 24" width={tamanho} height={tamanho} aria-hidden="true" focusable="false"
    fill={preenchido ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

export const IconeEditar = ({ tamanho = 14 }) => (
  <Svg tamanho={tamanho}><path d="M4 20h4L19 9l-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" /></Svg>
);

export const IconeLixeira = ({ tamanho = 14 }) => (
  <Svg tamanho={tamanho}>
    <path d="M4 7h16" /><path d="M9 7V4.5h6V7" /><path d="M6.5 7l1 13h9l1-13" /><path d="M10 11v5.5M14 11v5.5" />
  </Svg>
);

export const IconeEstrela = ({ preenchida = false, tamanho = 16 }) => (
  <Svg tamanho={tamanho} preenchido={preenchida}>
    <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8L3.5 9.7l5.9-.9L12 3.5z" />
  </Svg>
);

// "Painel" do projeto: janela com a tabela a esquerda e a lateral a direita.
export const IconePainel = ({ tamanho = 16 }) => (
  <Svg tamanho={tamanho}>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" /><path d="M15 4.5v15" />
    <path d="M6.5 9h5.5M6.5 12h5.5M6.5 15h5.5" />
  </Svg>
);

export const IconeMais = ({ tamanho = 16 }) => (
  <svg viewBox="0 0 24 24" width={tamanho} height={tamanho} aria-hidden="true" focusable="false" fill="currentColor">
    <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
  </svg>
);

// Iniciais e cor de cada pessoa saíram daqui para utils/avatarUsuario.js (o
// mesmo padrão da navbar, do Ranking e do Perfil); seguem exportadas para quem
// já importava de ./componentes.
export { corDoUsuario, iniciais };

export const Avatares = ({ usuarios = [], rodando = [], compacto = false }) => {
  useTranslation('tasks');
  const ativos = new Set(rodando.map((u) => u.id));
  return (
    <span className={`tf-avatares${compacto ? ' compacto' : ''}`}>
      {usuarios.map((u) => (
        <AvatarUsuario
          key={u.id}
          usuario={u}
          className={`tf-avatar${ativos.has(u.id) ? ' rodando' : ''}`}
          title={`${u.nome}${ativos.has(u.id) ? tt('trackingTimeNow') : ''}`}
        />
      ))}
    </span>
  );
};

export const BadgePrioridade = ({ prioridade }) => {
  useTranslation('tasks');
  return (<span className={`tf-badge ${prioridade}`}>{rotuloPrioridade(prioridade)}</span>);
};

// O aviso de hora extra e longo: fica mais tempo que um toast comum.
const AVISO_HORA_EXTRA_MS = 9000;

// Play/pause de uma tarefa para o usuario logado.
export const BotaoPlay = ({ tarefa, usuarioId }) => {
  useTranslation('tasks');
  const [enviando, setEnviando] = useState(false);
  const rodandoPorMim = (tarefa.em_andamento_por || []).some((u) => Number(u.id) === Number(usuarioId));
  const souResponsavel = (tarefa.responsaveis || []).some((u) => Number(u.id) === Number(usuarioId));
  const bloqueado = !souResponsavel || tarefa.status === 'concluida';

  const alternar = async (e) => {
    e.stopPropagation();
    setEnviando(true);
    try {
      if (rodandoPorMim) await pausarPlay();
      else {
        const resultado = await darPlay(tarefa.id);
        if (resultado?.fora_expediente) {
          notifyToast({ mensagem: tt('overtimeTimerNotice'), tipo: 'warning', duration: AVISO_HORA_EXTRA_MS });
        }
      }
    } catch (err) {
      notifyToast({ mensagem: mensagemDeErro(err), tipo: 'error' });
    } finally {
      setEnviando(false);
    }
  };

  let dica = rodandoPorMim ? tt('pause') : tt('startTimer');
  if (!souResponsavel) dica = tt('onlyAssigneesCanStartTheTimer');
  else if (tarefa.status === 'concluida') dica = tt('taskCompleted');

  return (
    <button
      type="button"
      className={`tf-play${rodandoPorMim ? ' rodando' : ''}`}
      onClick={alternar}
      disabled={bloqueado || enviando}
      title={dica}
      aria-label={dica}
    >
      {rodandoPorMim ? <IconePausa /> : <IconePlay />}
    </button>
  );
};

export const TempoEstimativa = ({ tarefa }) => {
  useTranslation('tasks');
  const estimativa = Number(tarefa.estimativa_minutos) || 0;
  const texto = formatarDuracao(useTempoAoVivo(tarefa));
  if (!estimativa) return <span>{texto}</span>;
  return <span title={tt('actualEstimated')}>{texto} / {formatarDuracao(estimativa * 60)}</span>;
};

export const LinhaTarefa = ({ tarefa, usuarioId, onAbrir, mostrarProjeto = true }) => {
  useTranslation('tasks');
  return (
    <div className={`tf-linha${rodandoAgora(tarefa) ? ' rodando' : ''}`}>
      <BotaoPlay tarefa={tarefa} usuarioId={usuarioId} />
      <div className="tf-linha-corpo" onClick={() => onAbrir(tarefa.id)} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAbrir(tarefa.id); } }}>
        <div className="tf-linha-titulo">
          #{tarefa.numero} {tarefa.titulo}
        </div>
        <div className="tf-meta">
          {mostrarProjeto && (
            <span>
              <span className="tf-projeto-ponto" style={{ background: tarefa.projeto_cor }} />
              {tarefa.projeto_nome}
            </span>
          )}
          <span>{tarefa.equipe_nome}</span>
          {tarefa.tarefa_pai_id && <span>{tt('subtask')}</span>}
          <BadgePrioridade prioridade={tarefa.prioridade} />
          {tarefa.status === 'concluida' && <span className="tf-badge concluida">{tt('completed')}</span>}
          {atrasada(tarefa) && <span className="tf-badge atrasada">{tt('overdue')}</span>}
          {(tarefa.data_inicio || tarefa.data_fim) && (
            <span>{formatarData(tarefa.data_inicio)} → {formatarData(tarefa.data_fim)}</span>
          )}
          <Avatares usuarios={tarefa.responsaveis} rodando={tarefa.em_andamento_por} />
        </div>
      </div>
      <div className="tf-linha-tempo">
        <TempoEstimativa tarefa={tarefa} />
      </div>
    </div>
  );
};

// Formulario de nova tarefa ou subtarefa.
//  - tarefa: equipe entre as minhas (admin: qualquer ativa);
//  - subtarefa: qualquer equipe ativa (tarefa generica -> subtarefas por equipe).
export const NovaTarefaModal = ({ contexto, equipes, projetoId = null, pai = null, onClose, onCriada }) => {
  useTranslation('tasks');
  const [projetos, setProjetos] = useState([]);
  const [projeto, setProjeto] = useState(projetoId || '');
  const equipesPossiveis = useMemo(() => {
    const ativas = equipes.filter((e) => e.ativo);
    if (pai || contexto.ehAdmin) return ativas;
    const minhas = new Set(contexto.minhasEquipes.map((e) => e.id));
    return ativas.filter((e) => minhas.has(e.id));
  }, [equipes, contexto, pai]);

  // Padrao: uma equipe de que o usuario e membro (o admin ve todas, mas
  // comeca na dele); subtarefa comeca na equipe da mae.
  const [equipeId, setEquipeId] = useState(() => {
    if (pai) return pai.equipe_id;
    const minhas = new Set(contexto.minhasEquipes.map((e) => Number(e.id)));
    const padrao = equipesPossiveis.find((e) => minhas.has(Number(e.id))) || equipesPossiveis[0];
    return padrao?.id || '';
  });
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [prioridade, setPrioridade] = useState('media');
  const [status, setStatus] = useState('a_fazer');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [estimativa, setEstimativa] = useState('');
  const [responsaveis, setResponsaveis] = useState(() => new Set());
  const [enviando, setEnviando] = useState(false);
  const idBase = useId();
  const idTitulo = `${idBase}-titulo`;
  const idResponsaveis = `${idBase}-responsaveis`;
  // Foco no titulo (autoFocus), Tab preso, Esc fecha e o foco volta a quem abriu.
  const caixaRef = useFocoPreso(true, { aoFechar: onClose });

  useEffect(() => {
    if (projetoId || pai) return;
    api.get('/tarefas/projetos')
      .then(({ data }) => setProjetos(data))
      .catch(() => setProjetos([]));
  }, [projetoId, pai]);

  const opcoesDeProjeto = useMemo(() => projetos
    .filter((p) => !p.arquivado)
    .map((p) => ({ value: String(p.id), label: p.nome })), [projetos]);

  const equipe = equipes.find((e) => Number(e.id) === Number(equipeId));
  // Lider da equipe e admin escolhem qualquer pessoa da equipe e o status
  // "Concluida"; o colaborador so pode se atribuir.
  const distribui = lideraEquipe(contexto, equipeId);
  const membros = (equipe?.membros || []).filter((m) => distribui || Number(m.id) === Number(contexto.usuario.id));

  // Trocar a equipe limpa os responsaveis: eles precisam ser membros dela.
  useEffect(() => {
    setResponsaveis(new Set());
  }, [equipeId]);

  const alternarResponsavel = (id) => {
    setResponsaveis((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const salvar = async (e) => {
    e.preventDefault();
    const minutos = textoParaMinutos(estimativa);
    if (Number.isNaN(minutos)) {
      notifyToast({ mensagem: tt('invalidEstimateUseHoursMinutesEG230'), tipo: 'error' });
      return;
    }
    const corpo = {
      equipe_id: Number(equipeId),
      titulo,
      descricao: descricao || null,
      prioridade,
      status,
      data_inicio: dataInicio || null,
      data_fim: dataFim || null,
      estimativa_minutos: minutos,
      responsaveis: [...responsaveis]
    };
    setEnviando(true);
    try {
      const url = pai
        ? `/tarefas/tarefas/${pai.id}/subtarefas`
        : `/tarefas/projetos/${projeto}/tarefas`;
      const { data } = await api.post(url, corpo);
      notifyToast({ mensagem: tt('taskCreated', { v0: data.numero }), tipo: 'success' });
      onCriada?.(data);
    } catch (err) {
      notifyToast({ mensagem: mensagemDeErro(err), tipo: 'error' });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="tf-modal-fundo" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="tf-modal" onSubmit={salvar} ref={caixaRef} role="dialog" aria-modal="true" aria-labelledby={idTitulo} tabIndex={-1}>
        <h3 id={idTitulo}>{pai ? tt('newSubtaskOf', { v0: pai.numero }) : tt('newTask')}</h3>

        {/* Projeto com busca: a lista costuma ser longa. O menu e "fixed" para
            nao ser cortado pela rolagem do modal. */}
        {!projetoId && !pai && (
          <div className="tf-campo">
            <label htmlFor="tf-nova-tarefa-projeto">{tt('project')}</label>
            <SearchableSelect
              id="tf-nova-tarefa-projeto"
              className="searchable-select--flush"
              options={opcoesDeProjeto}
              value={projeto}
              onChange={setProjeto}
              placeholder={tt('searchProject')}
              noOptionsMessage={tt('noProjectFound')}
              isClearable={false}
              menuPosition="fixed"
              menuPlacement="auto"
            />
          </div>
        )}

        <label className="tf-campo">
          <span>{tt('team')} {pai ? tt('canBeAnotherTeam') : ''}</span>
          <select value={equipeId} onChange={(e) => setEquipeId(e.target.value)} required>
            <option value="">{tt('select')}</option>
            {equipesPossiveis.map((eq) => (
              <option key={eq.id} value={eq.id}>{eq.nome}</option>
            ))}
          </select>
        </label>
        {!equipesPossiveis.length && (
          <div className="tf-aviso">{tt('youAreNotPartOfAnyTeamYetAsk')}</div>
        )}

        <label className="tf-campo">
          <span>{tt('title')}</span>
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={200} required autoFocus />
        </label>

        <label className="tf-campo">
          <span>{tt('description')}</span>
          <textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} />
        </label>

        <div className="tf-campos">
          <label className="tf-campo">
            <span>{tt('priority')}</span>
            <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
              {PRIORIDADES.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
            </select>
          </label>
          <label className="tf-campo">
            <span>{tt('status')}</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS.filter((s) => distribui || s.valor !== 'concluida')
                .map((s) => <option key={s.valor} value={s.valor}>{s.rotulo}</option>)}
            </select>
          </label>
          <label className="tf-campo">
            <span>{tt('start')}</span>
            <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          </label>
          <label className="tf-campo">
            <span>{tt('end')}</span>
            <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </label>
          <label className="tf-campo">
            <span>{tt('estimateHMm')}</span>
            <input value={estimativa} onChange={(e) => setEstimativa(e.target.value)} placeholder={tt('eG230')} />
          </label>
        </div>

        <div className="tf-campo">
          <span id={idResponsaveis}>{distribui ? tt('assigneesTeamMembers') : tt('assigneeYouCanAssignYourselfTheLeaderAssignsOthers')}</span>
          {membros.length ? (
            <div className="tf-checks" role="group" aria-labelledby={idResponsaveis}>
              {membros.map((m) => (
                <label key={m.id}>
                  <input type="checkbox" checked={responsaveis.has(m.id)} onChange={() => alternarResponsavel(m.id)} />
                  {m.nome}
                </label>
              ))}
            </div>
          ) : (
            <small>{distribui ? tt('selectATeamWithALeaderOrMembers') : tt('youAreNotPartOfThisTeamItsLeader')}</small>
          )}
        </div>

        <div className="tf-acoes" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="tf-btn secundario" onClick={onClose}>{tt('cancel')}</button>
          <button type="submit" className="tf-btn" disabled={enviando || !equipeId || (!projetoId && !pai && !projeto)}>
            {enviando ? tt('saving') : tt('create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export { rotuloStatus, minutosParaTexto };

// ------------------------------------------------------- anexos de imagem

// As imagens sao baixadas pela API com o token (uma <img src> comum nao manda
// o Authorization). Cache por id enquanto a pagina estiver aberta.
const cacheDeImagens = new Map();

const carregarImagem = (id) => {
  if (!cacheDeImagens.has(id)) {
    const promessa = api.get(`/tarefas/anexos/${id}`, { responseType: 'blob' })
      .then(({ data }) => URL.createObjectURL(data))
      .catch((err) => {
        cacheDeImagens.delete(id);
        throw err;
      });
    cacheDeImagens.set(id, promessa);
  }
  return cacheDeImagens.get(id);
};

export const Lightbox = ({ src, alt, onFechar }) => {
  // Esc fecha (so a imagem, nao o painel por baixo) e o foco volta ao anexo.
  const caixaRef = useFocoPreso(true, { aoFechar: onFechar });
  return (
    <div className="tf-lightbox" onClick={onFechar} ref={caixaRef} role="dialog" aria-modal="true" aria-label={alt} tabIndex={-1}>
      <img src={src} alt={alt} />
    </div>
  );
};

export const AnexoImagem = ({ anexo }) => {
  useTranslation('tasks');
  const [src, setSrc] = useState(null);
  const [falhou, setFalhou] = useState(false);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    let ativo = true;
    carregarImagem(anexo.id)
      .then((url) => { if (ativo) setSrc(url); })
      .catch(() => { if (ativo) setFalhou(true); });
    return () => { ativo = false; };
  }, [anexo.id]);

  return (
    <>
      <button type="button" className="tf-anexo" title={anexo.nome} onClick={() => src && setAberto(true)}>
        {src && <img src={src} alt={anexo.nome} />}
        {!src && <small style={{ padding: 6 }}>{falhou ? tt('unavailable') : '…'}</small>}
      </button>
      {aberto && <Lightbox src={src} alt={anexo.nome} onFechar={() => setAberto(false)} />}
    </>
  );
};

export const TIPOS_DE_IMAGEM = ['image/png', 'image/jpeg'];
export const MAX_ANEXOS = 5;
export const MAX_BYTES_ANEXO = 5 * 1024 * 1024;
// Arquivos da aba Anexos: qualquer tipo, ate 15 MB (o servidor confere de novo).
export const MAX_BYTES_ARQUIVO = 15 * 1024 * 1024;
